/**
 * IntentSheet — conversational, one-question-per-screen intent capture.
 *
 * Fires when a high-intent action (Post Lead, Connect, Claim Offer, Book
 * Consultation, Apply, Message Institution) needs more profile signal than
 * the user has provided. Autosaves each step via PATCH /api/intent/me.
 *
 * Reuses the same visual rhythm as `OnboardingPage` (progress bar + per-step
 * fade) — keeps the UX vocabulary consistent. Browsing the platform remains
 * free; this sheet only opens on intent.
 *
 * Imperative API:
 *   const { openIntentSheet } = useIntent();
 *   openIntentSheet({ action: 'post_lead', onComplete: () => doTheThing() });
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { ArrowLeft, ArrowRight, Check, X, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '@/App';

/** All 10 required + 5 optional steps. Order matters — shortest-yielding
 *  questions first so the perceived effort stays low. */
const STEPS = [
  { key: 'target_country',         q: 'Which country do you want to study in?',
    type: 'choice', options: ['Australia', 'Canada', 'UK', 'USA', 'New Zealand', 'Germany', 'Ireland'] },
  { key: 'study_level',            q: 'What level are you applying for?',
    type: 'choice', options: ['Bachelor', 'Master', 'Diploma', 'PhD', 'Vocational / Certificate'] },
  { key: 'preferred_field',        q: 'What do you want to study?',
    type: 'choice', options: ['Information Technology', 'Business / Commerce', 'Health / Nursing', 'Engineering', 'Education', 'Arts / Design', 'Hospitality', 'Trades'] },
  { key: 'intake_period',          q: 'When are you planning to start?',
    type: 'choice', options: ['Feb 2026', 'Jul 2026', 'Feb 2027', 'Flexible'] },
  { key: 'budget_range',           q: "What's your approximate yearly budget?",
    type: 'choice', options: ['<20000', '20000-30000', '30000-45000', '45000+'] },
  { key: 'current_qualification',  q: "What's your highest qualification so far?",
    type: 'choice', options: ['Year 12 / High School', 'Diploma', 'Bachelor', 'Master', 'PhD'] },
  { key: 'passport_country',       q: 'Which passport do you hold?',
    type: 'text', placeholder: 'e.g. India' },
  { key: 'english_test_status',    q: 'Have you taken an English test?',
    type: 'choice', options: ['Yes — already taken', 'Scheduled', 'Not yet', 'Exempt'] },
  { key: 'preferred_city',         q: "Any preferred city? (optional but speeds up matching)",
    type: 'text', placeholder: 'e.g. Sydney', optional: true },
  { key: 'timeline_urgency',       q: 'How urgent is this for you?',
    type: 'choice', options: ['Immediate (this intake)', '3-6 months', '6-12 months', 'Just exploring'] },
];

const IntentContext = createContext(null);

export function IntentProvider({ children }) {
  const [sheetState, setSheetState] = useState(null);
  // {action, onComplete, onCancel} or null

  const openIntentSheet = useCallback((opts) => {
    setSheetState({
      action: opts?.action || 'post_lead',
      onComplete: opts?.onComplete || null,
      onCancel: opts?.onCancel || null,
    });
  }, []);

  const closeSheet = useCallback(() => setSheetState(null), []);

  return (
    <IntentContext.Provider value={{ openIntentSheet, closeSheet }}>
      {children}
      {sheetState && (
        <IntentSheetModal
          action={sheetState.action}
          onComplete={() => {
            const cb = sheetState.onComplete;
            setSheetState(null);
            if (cb) cb();
          }}
          onCancel={() => {
            const cb = sheetState.onCancel;
            setSheetState(null);
            if (cb) cb();
          }}
        />
      )}
    </IntentContext.Provider>
  );
}

export function useIntent() {
  const ctx = useContext(IntentContext);
  if (!ctx) {
    // Fail soft so a page that doesn't wrap in IntentProvider still works
    // (the high-intent action just proceeds without the sheet).
    return {
      openIntentSheet: ({ onComplete } = {}) => onComplete && onComplete(),
      closeSheet: () => {},
    };
  }
  return ctx;
}

/** Imperative helper for places that can't use the hook directly.
 *  Reads the current strength + action threshold and either proceeds or
 *  returns false (caller is responsible for opening the sheet). */
export async function checkIntentAction(action) {
  try {
    const res = await axios.post(`${API}/intent/check-action`, { action });
    return res.data; // { allowed, strength, required, missing }
  } catch {
    // If the user is not signed in (or any error), be permissive — the
    // backend's hard gates (require_role) will still enforce auth.
    return { allowed: true, strength: { score: 0, level: 'Incomplete' }, missing: [] };
  }
}

/* ───────────── Internal modal — not exported ───────────── */
function IntentSheetModal({ action, onComplete, onCancel }) {
  const { user } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [profile, setProfile] = useState({});
  const [strength, setStrength] = useState({ score: 0, level: 'Incomplete' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load existing intent on mount so we skip questions already answered.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    axios.get(`${API}/intent/me`).then(res => {
      if (!alive) return;
      const p = res.data?.profile || {};
      setProfile(p);
      setStrength(res.data?.strength || strength);
      // Jump to the first unanswered required step.
      const firstEmpty = STEPS.findIndex(s => !s.optional && !p[s.key]);
      setStepIdx(firstEmpty === -1 ? STEPS.length - 1 : firstEmpty);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveField = async (key, value) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/intent/me`, { [key]: value });
      setProfile(res.data?.profile || profile);
      setStrength(res.data?.strength || strength);
    } catch (e) {
      toast.error('Could not save — try again');
    } finally {
      setSaving(false);
    }
  };

  const headerLabel = {
    post_lead: 'Tell us what you need',
    connect: 'Quick intro for the agent',
    claim_offer: 'Confirm a few details',
    book_consult: 'Quick profile for the agent',
    message_institution: 'Quick context for the institution',
    apply: 'Application essentials',
  }[action] || 'Tell us about you';

  const step = STEPS[stepIdx];
  const value = step ? profile[step.key] : null;

  const canContinue = !!value || step?.optional;

  const handleNext = async () => {
    if (value !== undefined && value !== null && value !== '') {
      await saveField(step.key, value);
    }
    if (stepIdx >= STEPS.length - 1) {
      // Finished — see if we cleared the action's threshold and complete.
      const final = await checkIntentAction(action);
      if (final?.allowed) {
        toast.success('Profile updated — continuing');
        onComplete();
      } else {
        toast.info(`A few more details unlock this. ${Math.max(0, (final?.required || 0) - (final?.strength?.score || 0))}% to go.`);
        // Fall back to first unanswered required field.
        const firstEmpty = STEPS.findIndex(s => !s.optional && !profile[s.key] && !value);
        setStepIdx(firstEmpty === -1 ? 0 : firstEmpty);
      }
      return;
    }
    setStepIdx(stepIdx + 1);
  };

  const handlePrev = () => setStepIdx(Math.max(0, stepIdx - 1));

  const handleSetLocal = (v) => setProfile(p => ({ ...p, [step.key]: v }));

  return (
    <div className="fixed inset-0 z-[150] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="intent-sheet">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col">
        {/* Header — progress + close */}
        <div className="px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#1A7A6E]" />
              <h2 className="text-base font-black text-[#0F2240]">{headerLabel}</h2>
            </div>
            <button
              onClick={onCancel}
              className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
              data-testid="intent-close"
            >
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>
          <div className="flex gap-1">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`h-1 flex-1 rounded-full transition-all ${i <= stepIdx ? 'bg-[#1A7A6E]' : 'bg-slate-200'}`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-slate-400 mt-1.5 font-mono">
            <span>Step {stepIdx + 1} of {STEPS.length}</span>
            <span data-testid="intent-strength">{strength.score}% · {strength.level}</span>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 pt-6 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
            </div>
          ) : step ? (
            <div className="animate-fade-in">
              <p className="text-lg font-bold text-[#0F2240] leading-snug mb-1.5">{step.q}</p>
              <p className="text-xs text-slate-500 mb-5">
                {step.optional
                  ? 'Optional. Skip if you prefer.'
                  : 'Students with complete profiles receive better matches and faster responses.'}
              </p>
              {step.type === 'choice' ? (
                <div className="grid grid-cols-1 gap-2">
                  {step.options.map(opt => {
                    const active = value === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => handleSetLocal(opt)}
                        className={`px-4 py-3 rounded-xl border text-left text-sm font-semibold transition-all active:scale-[0.98] ${
                          active
                            ? 'bg-[#1A7A6E] text-white border-[#1A7A6E]'
                            : 'bg-white text-[#0F2240] border-slate-200 hover:border-[#1A7A6E]'
                        }`}
                        data-testid={`intent-opt-${step.key}-${opt}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <input
                  value={value || ''}
                  onChange={e => handleSetLocal(e.target.value)}
                  placeholder={step.placeholder}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm font-medium text-[#0F2240] placeholder:text-slate-300 focus:outline-none focus:border-[#1A7A6E]"
                  data-testid={`intent-input-${step.key}`}
                  autoFocus
                />
              )}
            </div>
          ) : null}
        </div>

        {/* Footer — nav */}
        <div className="px-5 py-4 border-t border-slate-100 flex items-center gap-2">
          {stepIdx > 0 && (
            <button
              onClick={handlePrev}
              className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-bold flex items-center gap-1"
              data-testid="intent-prev"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!canContinue || saving}
            className="flex-1 py-2.5 rounded-xl bg-[#1A7A6E] hover:bg-[#16685D] text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
            data-testid="intent-next"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {stepIdx >= STEPS.length - 1 ? 'Continue' : 'Next'}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
